/**
 * React Query hooks for Job operations.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import jobService from '../services/job.service';

interface UseJobListQueryParams {
  workspaceId: string | undefined;
  idToken: string | undefined;
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  // Filter params
  status?: string;
  positionIds?: string[];
  campaignIds?: string[];
  pipelineId?: string;
  search?: string;
  excludeCvId?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const useJobListQuery = ({
  workspaceId,
  idToken,
  page = 1,
  size = 10,
  sortBy = 'created_at',
  order = 'desc',
  status,
  positionIds,
  campaignIds,
  pipelineId,
  search,
  excludeCvId,
  enabled = true,
  refetchInterval = 5000,
}: UseJobListQueryParams) => {
  return useQuery({
    queryKey: [
      'jobs',
      workspaceId,
      page,
      size,
      sortBy,
      order,
      status,
      // Serialize arrays for stable queryKey comparison
      positionIds?.sort().join(','),
      campaignIds?.sort().join(','),
      pipelineId,
      search,
      excludeCvId,
    ],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }

      return await jobService.listJobs(workspaceId, idToken, {
        page,
        size,
        sortBy,
        order,
        status,
        positionIds,
        campaignIds,
        pipelineId,
        search,
        excludeCvId,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData, // Keep old data visible while new query loads
    refetchInterval,
    refetchIntervalInBackground: true,
    staleTime: 2000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseJobKanbanQueryParams {
  jobId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const useJobKanbanQuery = ({
  jobId,
  workspaceId,
  idToken,
  enabled = true,
  refetchInterval = 5000,
}: UseJobKanbanQueryParams) => {
  return useQuery({
    queryKey: ['job-kanban', jobId, workspaceId],
    queryFn: async () => {
      if (!jobId || !workspaceId || !idToken) {
        throw new Error('Job ID, Workspace ID and token are required');
      }

      return await jobService.getJobKanban(jobId, workspaceId, idToken);
    },
    enabled: enabled && !!jobId && !!workspaceId && !!idToken,
    refetchInterval,
    refetchIntervalInBackground: true,
    staleTime: 2000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseJobAssignmentsQueryParams {
  jobId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
}

export const useJobAssignmentsQuery = ({
  jobId,
  workspaceId,
  idToken,
  enabled = true,
}: UseJobAssignmentsQueryParams) => {
  return useQuery({
    queryKey: ['job-assignments', jobId, workspaceId],
    queryFn: async () => {
      if (!jobId || !workspaceId || !idToken) {
        throw new Error('Job ID, Workspace ID and token are required');
      }
      return await jobService.listJobAssignments(jobId, workspaceId, idToken);
    },
    enabled: enabled && !!jobId && !!workspaceId && !!idToken,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

interface UseUpdateCVRoundMutationParams {
  jobId: string;
  cvId: string;
  workspaceId: string | undefined;
  idToken: string | undefined;
}

export const useUpdateCVRoundMutation = ({
  jobId,
  cvId,
  workspaceId,
  idToken,
}: UseUpdateCVRoundMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roundId: string) => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token required');
      }
      return jobService.updateCVJob(
        jobId,
        cvId,
        workspaceId,
        idToken,
        { round_id: roundId }
      );
    },
    onSuccess: () => {
      // Invalidate CV detail to refresh round info
      queryClient.invalidateQueries({ queryKey: ['cv', cvId] });
    },
  });
};
